import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  FlatList,
  TouchableOpacity,
  TextInput,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useMuteStore } from '../store/muteStore';
import { colors } from '../utils/colors';
import { typography } from '../utils/typography';
import { spacing } from '../utils/theme';

export const MutedWordsScreen: React.FC = () => {
  const { mutedWords, muteWord, unmuteWord } = useMuteStore();
  const [newWord, setNewWord] = useState('');
  const mutedWordsArray = Array.from(mutedWords);

  const handleAddWord = () => {
    const word = newWord.trim();
    if (word === '') {
      Alert.alert('エラー', 'ワードを入力してください');
      return;
    }
    
    if (mutedWords.has(word)) {
      Alert.alert('エラー', 'このワードは既にミュートされています');
      return;
    }

    muteWord(word);
    setNewWord('');
  };

  const handleRemoveWord = (word: string) => {
    Alert.alert(
      'ミュート解除',
      `"${word}" のミュートを解除しますか?`,
      [
        { text: 'キャンセル', style: 'cancel' },
        {
          text: '解除',
          style: 'destructive',
          onPress: () => unmuteWord(word),
        },
      ]
    );
  };

  const renderItem = ({ item }: { item: string }) => (
    <View style={styles.wordItem}>
      <Text style={styles.wordText}>{item}</Text>
      <TouchableOpacity
        style={styles.removeButton}
        onPress={() => handleRemoveWord(item)}
      >
        <Ionicons name="close-circle" size={24} color={colors.error} />
      </TouchableOpacity>
    </View>
  );

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>ミュートしたワード</Text>
      </View>

      <View style={styles.inputContainer}>
        <TextInput
          style={styles.input}
          placeholder="ミュートするワードを入力"
          placeholderTextColor={colors.textSecondary}
          value={newWord}
          onChangeText={setNewWord}
          onSubmitEditing={handleAddWord}
          returnKeyType="done"
        />
        <TouchableOpacity style={styles.addButton} onPress={handleAddWord}>
          <Ionicons name="add" size={24} color={colors.background} />
        </TouchableOpacity>
      </View>

      {mutedWordsArray.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Ionicons
            name="text-outline"
            size={64}
            color={colors.textSecondary}
          />
          <Text style={styles.emptyText}>ミュートしたワードはありません</Text>
        </View>
      ) : (
        <FlatList
          data={mutedWordsArray}
          keyExtractor={item => item}
          renderItem={renderItem}
          contentContainerStyle={styles.listContent}
        />
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background,
  },
  headerTitle: {
    ...typography.h2,
    color: colors.text,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    gap: spacing.sm,
  },
  input: {
    flex: 1,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    ...typography.body,
    color: colors.text,
    backgroundColor: colors.background,
  },
  addButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  listContent: {
    padding: spacing.md,
  },
  wordItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    borderRadius: 8,
    backgroundColor: colors.card,
    marginBottom: spacing.sm,
  },
  wordText: {
    ...typography.body,
    color: colors.text,
    flex: 1,
  },
  removeButton: {
    padding: spacing.xs,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  emptyText: {
    ...typography.body,
    color: colors.textSecondary,
    marginTop: spacing.md,
    textAlign: 'center',
  },
});
